import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Globe, MoreVertical, Trash2, Pencil, Loader2, ExternalLink, Pause, Play, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import AddWebsiteDialog from "../components/AddWebsiteDialog";
import { StatusBadge } from "../components/StatusBadge";
import { staggerContainer, fadeUpItem } from "../components/Motion";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { fmtPct, fmtMs, timeAgo, siteVariant, siteDisplay } from "../lib/monitor";

function EditDialog({ site, onSaved, onClose }) {
  const [name, setName] = useState(site?.name || "");
  const [interval, setInterval] = useState(String(site?.interval || 5));
  const [tags, setTags] = useState((site?.tags || []).join(", "));
  const [keyword, setKeyword] = useState(site?.keyword || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name,
        interval: parseInt(interval, 10),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (site.monitor_type === "keyword") payload.keyword = keyword;
      const { data } = await api.put(`/websites/${site.id}`, payload);
      toast.success("Webapp updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!site} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-website-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Webapp</DialogTitle>
          <DialogDescription>Update the display name, tags or monitoring interval.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Display name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name-input" />
          </div>
          {site.monitor_type === "keyword" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Keyword ({site.keyword_match || "present"})</Label>
              <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} data-testid="edit-keyword-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Tags (comma separated)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="edit-tags-input" placeholder="production, api" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Monitoring interval</Label>
            <Select value={interval} onValueChange={setInterval}>
              <SelectTrigger data-testid="edit-interval-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["5", "10", "30", "60"].map((v) => (
                  <SelectItem key={v} value={v}>{v === "60" ? "1 hour" : `${v} minute${v === "1" ? "" : "s"}`}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="save-edit-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving && <Loader2 size={15} className="mr-2 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Webapps() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [tagFilter, setTagFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/websites");
      setSites(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const confirmDelete = async () => {
    try {
      await api.delete(`/websites/${deleting.id}`);
      toast.success(`${deleting.name} removed`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const togglePause = async (site) => {
    try {
      const { data } = await api.post(`/websites/${site.id}/pause`);
      toast.success(data.paused ? `${site.name} paused` : `${site.name} resumed`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    }
  };

  const allTags = useMemo(() => {
    const s = new Set();
    sites.forEach((w) => (w.tags || []).forEach((t) => s.add(t)));
    return Array.from(s);
  }, [sites]);

  const visible = useMemo(() => {
    let list = [...sites];
    if (query) list = list.filter((s) => (s.name + s.url + (s.tags || []).join(" ")).toLowerCase().includes(query.toLowerCase()));
    if (filter === "up") list = list.filter((s) => s.status === "UP" && !s.paused);
    if (filter === "down") list = list.filter((s) => s.status === "DOWN" && !s.paused);
    if (filter === "paused") list = list.filter((s) => s.paused);
    if (tagFilter !== "all") list = list.filter((s) => (s.tags || []).includes(tagFilter));
    if (sort === "uptime") list.sort((a, b) => (b.uptime_24h ?? -1) - (a.uptime_24h ?? -1));
    if (sort === "response") list.sort((a, b) => (a.last_response_time ?? 1e9) - (b.last_response_time ?? 1e9));
    return list;
  }, [sites, query, filter, sort, tagFilter]);

  return (
    <div className="space-y-6" data-testid="websites-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Webapps</h1>
          <p className="mt-1 text-sm text-slate-500">{sites.length} monitored</p>
        </div>
        <AddWebsiteDialog onAdded={load} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="website-search-input"
            placeholder="Search webapps..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36" data-testid="website-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="up">Up only</SelectItem>
            <SelectItem value="down">Down only</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
          </SelectContent>
        </Select>
        {allTags.length > 0 && (
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-40" data-testid="website-tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tags</SelectItem>
              {allTags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="website-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Recently added</SelectItem>
            <SelectItem value="uptime">Sort by uptime</SelectItem>
            <SelectItem value="response">Sort by response</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="websites-skeleton">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="border-slate-200 p-5">
              <Skeleton className="h-4 w-32" /><Skeleton className="mt-2 h-3 w-40" />
              <Skeleton className="mt-4 h-5 w-16 rounded-full" />
              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                <Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" />
              </div>
            </Card>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center" data-testid="websites-empty">
          <Globe className="mx-auto mb-3 text-slate-300" size={36} />
          <p className="text-sm text-slate-500">No webapps match your filters.</p>
        </Card>
      ) : (
        <motion.div variants={staggerContainer} initial="hidden" animate="show" className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((site) => (
            <motion.div key={site.id} variants={fadeUpItem} whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}>
            <Card
              data-testid={`webapp-card-${site.id}`}
              className="group border-slate-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/websites/${site.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">{site.name}</p>
                  <p className="truncate font-mono text-xs text-slate-400">{site.url}</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`webapp-menu-${site.id}`}>
                      <MoreVertical size={16} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => window.open(site.url, "_blank")}><ExternalLink size={14} className="mr-2" /> Visit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => togglePause(site)} data-testid={`pause-${site.id}`}>
                      {site.paused ? <><Play size={14} className="mr-2" /> Resume</> : <><Pause size={14} className="mr-2" /> Pause</>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditing(site)} data-testid={`edit-${site.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setDeleting(site)} className="text-red-600 focus:text-red-600" data-testid={`delete-${site.id}`}>
                      <Trash2 size={14} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <StatusBadge variant={siteDisplay(site).variant} pulse={!site.paused && site.status !== "UNKNOWN"}>{siteDisplay(site).label}</StatusBadge>
                <span className="text-xs text-slate-400">{timeAgo(site.last_checked)}</span>
              </div>
              {(site.monitor_type === "keyword" || (site.tags && site.tags.length > 0)) && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {site.monitor_type === "keyword" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-mono font-medium text-violet-700">
                      <Search size={10} /> keyword
                    </span>
                  )}
                  {(site.tags || []).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-mono text-slate-600">
                      <Tag size={10} /> {t}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Uptime 24h</p>
                  <p className="font-mono text-lg font-semibold text-slate-900">{fmtPct(site.uptime_24h)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Response</p>
                  <p className="font-mono text-lg font-semibold text-slate-900">{fmtMs(site.last_response_time)}</p>
                </div>
              </div>
            </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      {editing && (
        <EditDialog
          site={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this webapp?</AlertDialogTitle>
            <AlertDialogDescription>
              Monitoring for <span className="font-mono font-medium">{deleting?.name}</span> and all its history will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
